import React from 'react'
import { DealSettlement, NegotiationInstruction, PreparedOrderClearingDetail, SettlementVenue } from 'api'
import { DialogField, DialogRow } from 'view/components/inputs'
import { BilateralClearingSection } from './BilateralClearingSection'
import { DirectClearingSection } from './DirectClearingSection'
import { SponsoredClearingSection } from './SponsoredClearingSection'
import { SettlementVenueSelectboxField } from 'view/shared/entity'
import { FICC_CLEARING_HOUSE_ID, shouldResetCsd, tradingEntityClearingTypeFromSettlementVenue } from 'model'
import { Locale, Translation } from 'locale'
import { Nullable } from 'utils'
import { isNil } from 'lodash'

interface ClearingSectionProps {
  readOnly: boolean
  settlementVenue: Nullable<SettlementVenue>
  settlementVenueOptions: string[]
  clearingHouseId: string | null
  sponsorId: string | null
  csd: DealSettlement | null
  preparedOrderClearingDetails: PreparedOrderClearingDetail[]
  settlementVenueReadOnly: boolean
  allOrdersAreUs: boolean
  onChangeNegotiationDetails: (partialNegotiationInstruction: Partial<NegotiationInstruction>) => unknown
}

export const ClearingSection: React.FunctionComponent<ClearingSectionProps> = props => {
  const {
    readOnly,
    settlementVenue,
    settlementVenueOptions,
    clearingHouseId,
    sponsorId,
    csd,
    preparedOrderClearingDetails,
    settlementVenueReadOnly,
    allOrdersAreUs,
    onChangeNegotiationDetails
  } = props

  const isUsSponsoredClearing = (settlementVenue: SettlementVenue) => allOrdersAreUs && settlementVenue === SettlementVenue.Sponsored

  const onChangeSettlementVenue = (newSettlementVenue: SettlementVenue) => {
    if (newSettlementVenue !== settlementVenue) {
      onChangeNegotiationDetails({
        settlementVenue: newSettlementVenue,
        clearingType: tradingEntityClearingTypeFromSettlementVenue(newSettlementVenue),
        clearingHouseId: isUsSponsoredClearing(newSettlementVenue) ? FICC_CLEARING_HOUSE_ID : null,
        sponsorId: null,
        counterpartyInstructions: [],
        ...(shouldResetCsd(newSettlementVenue) && { csd: null })
      })
    }
  }

  const onChangeClearingDetails = (newClearingHouseId: string | null, newSponsorId: string | null) => {
    onChangeNegotiationDetails({ clearingHouseId: newClearingHouseId, sponsorId: newSponsorId })
  }

  const onChangeCsd = (csd: DealSettlement | null) => {
    onChangeNegotiationDetails({ csd })
  }

  return (
    <DialogRow>
      <DialogField>
        <SettlementVenueSelectboxField
          dataTestId={'ClearingSection-SettlementVenueSelectboxField-Settlement'}
          label={Locale.translate(Translation.TC_SETTLEMENT)}
          value={settlementVenue}
          optionKeys={settlementVenueOptions}
          readOnly={readOnly || settlementVenueReadOnly}
          onUpdate={onChangeSettlementVenue}
        />
      </DialogField>
      {isNil(settlementVenue) && (
        <>
          <DialogField />
          <DialogField />
        </>
      )}
      {(settlementVenue === SettlementVenue.Bilateral || settlementVenue === SettlementVenue.Triparty) && (
        <>
          <BilateralClearingSection
            readOnly={readOnly || settlementVenue !== SettlementVenue.Bilateral}
            csd={csd}
            onChangeCsd={onChangeCsd}
          />
          <DialogField />
        </>
      )}
      {settlementVenue === SettlementVenue.Direct && (
        <>
          <DirectClearingSection
            readOnly={readOnly}
            clearingHouseId={clearingHouseId}
            preparedOrderClearingDetails={preparedOrderClearingDetails}
            onChangeClearingDetails={onChangeClearingDetails}
          />
          <DialogField />
        </>
      )}
      {settlementVenue === SettlementVenue.Sponsored && (
        <SponsoredClearingSection
          readOnly={readOnly || allOrdersAreUs}
          clearingHouseId={clearingHouseId}
          sponsorId={sponsorId}
          preparedOrderClearingDetails={preparedOrderClearingDetails}
          onChangeClearingDetails={onChangeClearingDetails}
        />
      )}
    </DialogRow>
  )
}
